import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useBasePath } from '@/lib/config/nav-helpers';
import { PLANS, TRIAL_DAYS, formatPrice } from '@/lib/billing';

const FROM_PRICE = `${formatPrice(Math.min(...PLANS.map((p) => p.monthlyCents)))}/mo`;

interface SubscriptionStatus {
  plan: string;
  status: string;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  hasStripeCustomer: boolean;
}

/**
 * mode="urgent": trial ended / payment failed / ≤3 days left — outranks setup
 * prompts in the banner slot. mode="countdown": the quiet "N days left"
 * reminder, which must not bury onboarding.
 */
export function TrialBanner({ mode }: { mode: 'urgent' | 'countdown' }) {
  const navigate = useNavigate();
  const basePath = useBasePath();

  const { data } = useQuery({
    queryKey: ['billing-subscription-status'],
    queryFn: () => api<SubscriptionStatus>('/v1/billing/subscription'),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!data) return null;

  const { plan, status, trialDaysRemaining, trialEndsAt } = data;

  // Don't show banner for active paid subscriptions
  if (status === 'active' && plan !== 'trial') return null;

  // New workspace that hasn't started its card trial at Checkout yet.
  if (status === 'incomplete') {
    if (mode !== 'urgent') return null;
    return (
      <div className="bg-primary/5 border-b border-primary/10 px-4 py-2 flex items-center justify-between gap-3">
        <span className="text-2xs text-ink-muted">
          Start your {TRIAL_DAYS}-day free trial from {FROM_PRICE}. Card required; charged on day {TRIAL_DAYS + 1} unless you cancel.
        </span>
        <button
          onClick={() => navigate(`${basePath}/settings?tab=billing`)}
          className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 shadow-xs"
        >
          Choose a plan
        </button>
      </div>
    );
  }

  // Card trial (plan set by Stripe, status trialing): it converts on its own,
  // so say when the card is charged — never "subscribe or lose access".
  if (status === 'trialing' && plan !== 'trial') {
    if (mode !== 'countdown' || trialDaysRemaining === null || !trialEndsAt) return null;
    return (
      <div className="bg-surface-1 border-b border-border px-4 py-1.5 flex items-center justify-between gap-3">
        <span className="text-2xs text-ink-dim">
          Free trial · {trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} left · your card is charged on{' '}
          {new Date(trialEndsAt).toLocaleDateString()} unless you cancel
        </span>
        <button
          onClick={() => navigate(`${basePath}/settings?tab=billing`)}
          className="shrink-0 text-2xs text-primary hover:text-primary-bright font-medium"
        >
          Manage billing
        </button>
      </div>
    );
  }

  const isExpired = status === 'canceled' || status === 'unpaid' || status === 'past_due' ||
    (status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining <= 0);
  const isUrgent = trialDaysRemaining !== null && trialDaysRemaining <= 3;
  if ((mode === 'urgent') !== (isExpired || isUrgent)) return null;

  // Show subtle indicator for trials with 8-30 days remaining, urgent for ≤7
  if (status === 'trialing' && trialDaysRemaining !== null && trialDaysRemaining > 7) {
    return (
      <div className="bg-surface-1 border-b border-border px-4 py-1.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-2xs text-ink-dim">
            Free trial · {trialDaysRemaining} days remaining
          </span>
        </div>
        <button
          onClick={() => navigate(`${basePath}/settings?tab=billing`)}
          className="shrink-0 text-2xs text-primary hover:text-primary-bright font-medium"
        >
          View plans
        </button>
      </div>
    );
  }

  // Compute hours remaining for last-day precision
  const hoursRemaining = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60)))
    : null;

  if (isExpired) {
    return (
      <div className="bg-danger/10 border-b border-danger/20 px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-danger">Trial ended</span>
          <span className="mx-1 h-3 w-px bg-danger/20" />
          <span className="text-2xs text-ink-muted">
            {status === 'past_due'
              ? 'Payment failed — update your payment method to restore access.'
              : 'AI coaching, predictions, and briefings are paused. Subscribe to restore access.'}
          </span>
        </div>
        <button
          onClick={() => navigate(`${basePath}/settings?tab=billing`)}
          className="shrink-0 rounded-lg bg-danger text-on-fill px-4 py-1.5 text-xs font-semibold hover:opacity-90 shadow-xs"
        >
          {status === 'past_due' ? 'Update payment' : `Subscribe — from ${FROM_PRICE}`}
        </button>
      </div>
    );
  }

  if (trialDaysRemaining === null) return null;

  const timeLabel = trialDaysRemaining === 0 && hoursRemaining !== null
    ? `${hoursRemaining}h left`
    : trialDaysRemaining === 1
    ? 'Last day of trial'
    : `${trialDaysRemaining} days left`;

  const lossHint = trialDaysRemaining <= 3
    ? 'AI coaching, deal predictions, and morning briefings will pause when your trial ends.'
    : 'Subscribe to keep your data, predictions, and briefings after the trial ends.';

  return (
    <div className={`${isUrgent ? 'bg-warning/10 border-warning/20' : 'bg-primary/5 border-primary/10'} border-b px-4 py-2 flex items-center justify-between gap-3`}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-xs font-semibold ${isUrgent ? 'text-warning' : 'text-primary'}`}>
          {timeLabel}
        </span>
        <span className="mx-1 h-3 w-px bg-border" />
        <span className="text-2xs text-ink-muted hidden sm:inline">
          {lossHint}
        </span>
      </div>
      <button
        onClick={() => navigate(`${basePath}/settings?tab=billing`)}
        className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
          isUrgent
            ? 'bg-warning text-white hover:opacity-90 shadow-xs'
            : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
        }`}
      >
        {isUrgent ? `Subscribe — from ${FROM_PRICE}` : 'Choose a plan'}
      </button>
    </div>
  );
}
